import clsx from 'clsx'
import { forwardRef, useCallback } from 'react'
import wait from '@ancon/wildcat-utils/shared/wait'

import { MenuSection } from '../../types'
import SpotlightText from '../../../app/components/SpotlightText'
import Button from '../../../app/components/Button'
import useIsMobileDevice from '../../../app/hooks/useIsMobileDevice'
import useMountedRef from '../../../app/hooks/useMountedRef'

import styles from './OutletMenuHeader.module.scss'

type OutletMenuHeaderProps = {
  groups: MenuSection[]
  selectedGroupIndex: number
  selectedSubGroupIndex?: number
  onChangeGroup: (index: number) => void
  onChangeSubGroup: (index?: number) => void
  onStartScroll: () => void
}

function OutletMenuHeader(
  {
    groups,
    selectedGroupIndex,
    selectedSubGroupIndex,
    onChangeGroup,
    onChangeSubGroup,
    onStartScroll,
  }: OutletMenuHeaderProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const mounted = useMountedRef()
  const isMobile = useIsMobileDevice()
  const subGroups = groups[selectedGroupIndex]?.subGroups

  const scrollToElement = useCallback(
    async (element: HTMLElement) => {
      onStartScroll()
      // Add wait as a workaround - scrollIntoView doesn't work on mobile
      await wait(10)

      if (mounted) {
        // Touchable devices not working smooth transition as expected
        element.scrollIntoView({
          behavior: isMobile ? 'auto' : 'smooth',
        })
      }
    },
    [mounted, onStartScroll, isMobile],
  )

  function handleOnClickGroup(index: number) {
    onChangeGroup(index)
    // If has subgroups, select the first one
    if (groups[index].subGroups?.length) {
      onChangeSubGroup(0)
    }
    const element = document.getElementById(`group-${index}`)

    if (element && mounted) {
      scrollToElement(element)
    }
  }

  function handleOnChangeSubGroup(index?: number) {
    onChangeSubGroup(index)

    // Scroll sub menu
    const subElement = document.getElementById(
      `group-${selectedGroupIndex}-sub-group-${index}`,
    )
    if (subElement && mounted) {
      scrollToElement(subElement)
    }

    // Scroll header
    const element = document.getElementById(`sub-menu-${index}`)
    const container = document.getElementById('sub-menus')

    if (container && element) {
      container.scrollTo({ left: element.offsetLeft, behavior: 'smooth' })
    }
  }

  return (
    <div className={styles.container} ref={ref}>
      <hr />
      <div className={clsx(styles.wrapper, styles.groupWrapper)}>
        {groups.map((group, index) => (
          <SpotlightText
            key={group.id}
            text={group.name}
            onClick={() => handleOnClickGroup(index)}
            active={selectedGroupIndex === index}
            className={clsx({ [styles.active]: selectedGroupIndex === index })}
          />
        ))}
      </div>
      <hr />
      {subGroups && subGroups?.length > 0 && (
        <>
          <div
            id="sub-menus"
            className={clsx(styles.wrapper, styles.subGroupWrapper)}
          >
            {subGroups.map((subGroup, index) => (
              <Button
                key={subGroup.id}
                variant="secondary"
                className={clsx({
                  [styles.selectedButton]: selectedSubGroupIndex === index,
                })}
                onClick={() => handleOnChangeSubGroup(index)}
                id={`sub-menu-${index}`}
              >
                {subGroup.name}
              </Button>
            ))}
          </div>
          <hr />
        </>
      )}
    </div>
  )
}

export default forwardRef(OutletMenuHeader)
